import { useState, useEffect } from "react";
import { authFetch } from "../lib/authFetch";
import { useClientAuth } from "../lib/auth-context";
import { useClientRouter } from "../lib/router-context";
import {
  Bot, ShoppingCart, LifeBuoy, Newspaper, Mail, Phone, User,
  Loader2, AlertCircle, Building2, Calendar, MapPin, Cpu,
} from "lucide-react";
import { getRobotClientStatus, ROBOT_CLIENT_STATUS_VALUES } from "@shared/robotStatusConfig";

function formatNumeroClient(n: number | null | undefined): string {
  if (!n) return "";
  return `JBT-CLI-${String(n).padStart(4, "0")}`;
}

function formatDate(d: string | null | undefined): string {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

interface DashboardData {
  client: {
    id: string;
    companyName: string;
    nomCommercial: string | null;
    numeroClient: number | null;
    logoUrl: string | null;
    address: string | null;
    contractType: string | null;
    contractStartDate: string | null;
    contractEndDate: string | null;
  };
  robots: Array<{
    id: string;
    nom: string;
    modele: string | null;
    serialNumber: string | null;
    macAddress: string | null;
    statut: string | null;
    imageUrl: string | null;
    purchaseDate: string | null;
  }>;
  robotStatusCounts: {
    actif: number;
    en_reparation: number;
    hors_service: number;
  };
  commandesEnCours: number | null;
  ticketsOuverts: number | null;
  actualites: Array<{
    id: string;
    titre: string;
    accroche: string | null;
    type: string | null;
    imageUrl: string | null;
    createdAt: string;
  }>;
  commercial: {
    id: string;
    displayName: string | null;
    email: string | null;
    phone: string | null;
    avatarUrl: string | null;
  } | null;
  technicienReferent: {
    id: string;
    displayName: string | null;
    email: string | null;
    phone: string | null;
    avatarUrl: string | null;
  } | null;
}

const GRADIENT = "linear-gradient(135deg, #4e63f0 0%, #2facf3 100%)";

const STATUT_COLORS: Record<string, string> = {
  [ROBOT_CLIENT_STATUS_VALUES.ACTIF]: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  [ROBOT_CLIENT_STATUS_VALUES.HORS_SERVICE]: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  en_maintenance: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  en_transit: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
};

const TYPE_NEWS_COLORS: Record<string, string> = {
  nouveaute: "bg-blue-100 text-blue-700",
  information: "bg-slate-100 text-slate-600",
  promotion: "bg-orange-100 text-orange-700",
  alerte: "bg-red-100 text-red-700",
};

function KpiCard({ icon: Icon, label, value, sub }: { icon: any; label: string; value: number | string; sub?: string }) {
  return (
    <div className="bg-card border border-border/60 rounded-xl p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: GRADIENT }}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold text-foreground tabular-nums">{value}</p>
        <p className="text-sm text-muted-foreground">{label}</p>
        {sub && <p className="text-xs text-muted-foreground/70">{sub}</p>}
      </div>
    </div>
  );
}

function InterlocuteurCard({ person, role }: { person: NonNullable<DashboardData["commercial"]>; role: string }) {
  return (
    <div className="flex items-center gap-3 p-4 bg-secondary/30 rounded-xl border border-border/40">
      {person.avatarUrl ? (
        <img src={person.avatarUrl} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
      ) : (
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0" style={{ background: GRADIENT }}>
          <User className="w-5 h-5" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground truncate">{person.displayName}</p>
        <p className="text-xs text-muted-foreground">{role}</p>
        {person.email && (
          <a href={`mailto:${person.email}`} className="text-xs text-primary hover:underline flex items-center gap-1 mt-0.5">
            <Mail className="w-3 h-3" />{person.email}
          </a>
        )}
        {person.phone && (
          <a href={`tel:${person.phone}`} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
            <Phone className="w-3 h-3" />{person.phone}
          </a>
        )}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { client: clientInfo, user } = useClientAuth();
  const { navigate } = useClientRouter();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRobot, setSelectedRobot] = useState<DashboardData["robots"][0] | null>(null);

  useEffect(() => {
    authFetch("/api/espace-client/dashboard")
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setData)
      .catch(() => setError("Impossible de charger le tableau de bord."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex-1 flex items-center justify-center h-full">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="w-5 h-5" />
          <p>{error || "Données indisponibles."}</p>
        </div>
      </div>
    );
  }

  const companyName = data.client.nomCommercial || data.client.companyName;
  const numeroClient = formatNumeroClient(data.client.numeroClient);

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* En-tête société */}
      <div className="rounded-2xl overflow-hidden border border-border/60 shadow-sm">
        <div className="h-2 w-full" style={{ background: GRADIENT }} />
        <div className="bg-card p-5 flex items-center gap-4">
          {data.client.logoUrl ? (
            <img src={data.client.logoUrl} alt={companyName} className="w-14 h-14 rounded-xl object-contain border border-border/40 bg-secondary/20 shrink-0 p-1" />
          ) : (
            <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-lg shrink-0" style={{ background: GRADIENT }}>
              <Building2 className="w-7 h-7" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold text-foreground truncate">{companyName}</h1>
            <div className="flex items-center flex-wrap gap-3 mt-1">
              {numeroClient && (
                <span className="text-sm font-mono text-primary font-semibold">{numeroClient}</span>
              )}
              {data.client.address && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3 h-3" />{data.client.address}
                </span>
              )}
              {data.client.contractType && (
                <span className="text-xs text-muted-foreground flex items-center gap-1 capitalize">
                  <Calendar className="w-3 h-3" />Contrat {data.client.contractType}
                  {data.client.contractStartDate && ` depuis ${formatDate(data.client.contractStartDate)}`}
                </span>
              )}
            </div>
          </div>
          <div className="text-right text-sm text-muted-foreground shrink-0">
            <p>Bonjour{user?.firstName ? `, ${user.firstName}` : ""} 👋</p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          icon={Bot}
          label={data.robots.length === 1 ? "Robot" : "Robots"}
          value={data.robots.length}
          sub={[
            data.robotStatusCounts.actif > 0 && `${data.robotStatusCounts.actif} en service`,
            data.robotStatusCounts.en_reparation > 0 && `${data.robotStatusCounts.en_reparation} en réparation`,
            data.robotStatusCounts.hors_service > 0 && `${data.robotStatusCounts.hors_service} hors service`,
          ].filter(Boolean).join(" · ") || undefined}
        />
        <KpiCard
          icon={ShoppingCart}
          label="Commandes en cours"
          value={data.commandesEnCours ?? "—"}
          sub={data.commandesEnCours == null ? "Donnée indisponible" : "Données Zoho Inventory"}
        />
        <KpiCard
          icon={LifeBuoy}
          label="Tickets SAV ouverts"
          value={data.ticketsOuverts ?? "—"}
          sub={data.ticketsOuverts == null ? "Donnée indisponible" : undefined}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Robots */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-card border border-border/60 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
              <h2 className="font-semibold text-foreground flex items-center gap-2">
                <Bot className="w-4 h-4 text-primary" /> Mes robots
              </h2>
              <span className="text-xs text-muted-foreground">
                {data.robots.length === 1 ? "1 robot" : `${data.robots.length} robots`}
              </span>
            </div>
            {data.robots.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Bot className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Aucun robot enregistré pour le moment.</p>
              </div>
            ) : (
              <div className="divide-y divide-border/30">
                {data.robots.map(robot => (
                  <button
                    key={robot.id}
                    onClick={() => setSelectedRobot(selectedRobot?.id === robot.id ? null : robot)}
                    className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-secondary/30 transition-colors text-left"
                    data-testid={`row-robot-${robot.id}`}
                  >
                    {robot.imageUrl ? (
                      <img src={robot.imageUrl} alt={robot.modele || ""} className="w-10 h-10 rounded-lg object-contain bg-secondary/30 shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-secondary/50 flex items-center justify-center shrink-0">
                        <Cpu className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{robot.nom}</p>
                      {robot.modele && robot.modele !== robot.nom && <p className="text-xs text-muted-foreground">{robot.modele}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {robot.serialNumber && (
                        <span className="text-xs font-mono bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-md">
                          SN: {robot.serialNumber}
                        </span>
                      )}
                      {robot.statut && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUT_COLORS[robot.statut] || "bg-secondary text-muted-foreground"}`}>
                          {getRobotClientStatus(robot.statut).known
                            ? robot.statut.replace(/_/g, " ")
                            : getRobotClientStatus(robot.statut).label}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Fiche détail robot (lecture seule) */}
          {selectedRobot && (
            <div className="bg-card border border-border/60 rounded-xl p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-foreground">{selectedRobot.nom}</h3>
                <button onClick={() => setSelectedRobot(null)} className="text-muted-foreground hover:text-foreground text-xs">Fermer ✕</button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {selectedRobot.modele && <div><p className="text-muted-foreground text-xs">Modèle</p><p className="font-medium">{selectedRobot.modele}</p></div>}
                {selectedRobot.serialNumber && <div><p className="text-muted-foreground text-xs">Numéro de série</p><p className="font-mono">{selectedRobot.serialNumber}</p></div>}
                {selectedRobot.macAddress && <div><p className="text-muted-foreground text-xs">Adresse MAC</p><p className="font-mono text-xs">{selectedRobot.macAddress}</p></div>}
                {selectedRobot.purchaseDate && <div><p className="text-muted-foreground text-xs">Date d'achat</p><p>{formatDate(selectedRobot.purchaseDate)}</p></div>}
              </div>
            </div>
          )}

          {/* Actualités */}
          {data.actualites.length > 0 && (
            <div className="bg-card border border-border/60 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
                <h2 className="font-semibold text-foreground flex items-center gap-2">
                  <Newspaper className="w-4 h-4 text-primary" /> Actualités
                </h2>
              </div>
              <div className="divide-y divide-border/30">
                {data.actualites.map(actu => (
                  <div key={actu.id} className="px-5 py-4 flex gap-3" data-testid={`card-actu-${actu.id}`}>
                    {actu.imageUrl && (
                      <img src={actu.imageUrl} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {actu.type && (
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${TYPE_NEWS_COLORS[actu.type] || "bg-secondary text-muted-foreground"}`}>
                            {actu.type}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">{formatDate(actu.createdAt)}</span>
                      </div>
                      <p className="text-sm font-medium text-foreground">{actu.titre}</p>
                      {actu.accroche && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{actu.accroche}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Colonne droite — interlocuteurs */}
        <div className="space-y-4">
          {(data.commercial || data.technicienReferent) && (
            <div className="bg-card border border-border/60 rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-border/40">
                <h2 className="font-semibold text-foreground">Vos interlocuteurs</h2>
              </div>
              <div className="p-4 space-y-3">
                {data.commercial && (
                  <InterlocuteurCard person={data.commercial} role="Référent commercial" />
                )}
                {data.technicienReferent && (
                  <InterlocuteurCard person={data.technicienReferent} role="Référent technique" />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
